import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LAST_UPDATED = '26 April 2026';
const CONTROLLER   = 'ImagiSoft Ltd';
const CONTACT      = 'legal@imagisoft.co.uk';
const APP_NAME     = 'Win/R';
const WEBSITE      = 'https://win.imagisoft.co.uk';

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    body: `By creating an account on ${APP_NAME} or by accessing ${WEBSITE} (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.

These Terms form a binding legal agreement between you and ${CONTROLLER} ("we", "us", "our"), a company registered in England and Wales.

We reserve the right to update these Terms at any time. Material changes will be communicated by email or in-app notice at least 14 days in advance. Continued use after the effective date constitutes acceptance.`,
  },
  {
    id: 'eligibility',
    title: '2. Eligibility',
    body: `You must be at least 18 years old to create a trainer account. Client community accounts require users to be at least 13 years old (16 in the UK/EU).

By using the Service you represent that you meet the applicable age requirement and have the legal capacity to enter into this agreement. If you are accepting on behalf of a business, you represent that you have authority to bind that business.`,
  },
  {
    id: 'accounts',
    title: '3. Accounts & Security',
    body: `You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. You agree to:

• Provide accurate and current information when registering
• Keep your password secure and not share it with others
• Notify us immediately at ${CONTACT} if you suspect unauthorised access
• Not create accounts for others without their consent

We reserve the right to suspend or terminate accounts that violate these Terms.`,
  },
  {
    id: 'trainer-accounts',
    title: '4. Trainer Accounts & Subscriptions',
    body: `Trainer accounts require a paid subscription to access full features. By subscribing you agree to:

Billing
• Subscriptions are billed monthly in advance via the App Store (iOS) or Google Play (Android)
• Payment is charged to your app store account upon confirmation of purchase
• Subscriptions auto-renew unless cancelled at least 24 hours before the end of the current period

Free Trial
• New accounts may receive a 7-day free trial. Only one free trial per Apple ID / Google account
• If you do not cancel during the trial, your subscription will begin and you will be charged

Cancellation
• Cancel at any time through your device's subscription settings (iOS: Settings → Apple ID → Subscriptions; Android: Google Play → Subscriptions)
• Cancellation takes effect at the end of the current billing period — no partial refunds are issued
• Access continues until the end of the paid period

Early Adopter Rates
• If you were offered a locked-in early adopter rate, that rate applies for as long as your subscription remains active and uninterrupted. Cancellation forfeits the locked-in rate.`,
  },
  {
    id: 'client-accounts',
    title: '5. Client & Community Accounts',
    body: `Client community accounts are free and allow participation in the social feed, workout scheduling, and messaging with trainers.

You may only create a client account if invited by a trainer or if you register independently through the community signup flow. One account per person.`,
  },
  {
    id: 'acceptable-use',
    title: '6. Acceptable Use',
    body: `You agree not to use the Service to:

• Post content that is illegal, harmful, threatening, abusive, harassing, defamatory, or obscene
• Infringe any intellectual property, privacy, or other rights of any person
• Impersonate any person or entity, or misrepresent your affiliation
• Upload or transmit malware, viruses, or any code designed to disrupt or damage systems
• Scrape, crawl, or extract data from the Service without our written consent
• Circumvent any security or access controls
• Use the Service to send unsolicited commercial communications
• Engage in any activity that violates applicable laws or regulations

We may remove content and suspend or terminate accounts that violate this policy without prior notice.`,
  },
  {
    id: 'content',
    title: '7. User Content',
    body: `You retain ownership of content you upload to the Service ("User Content"), including posts, images, videos, and profile information.

By uploading User Content you grant ${CONTROLLER} a non-exclusive, worldwide, royalty-free licence to store, display, and distribute that content solely as necessary to provide the Service.

You represent that:
• You own the content or have the rights to upload it
• The content does not infringe any third-party rights
• The content complies with Section 6 (Acceptable Use)

We may remove User Content that violates these Terms. We are not obligated to back up User Content — you are responsible for maintaining your own copies.`,
  },
  {
    id: 'ip',
    title: '8. Intellectual Property',
    body: `The ${APP_NAME} platform, including its design, code, logos, and content (excluding User Content) is owned by or licensed to ${CONTROLLER} and is protected by copyright, trademark, and other intellectual property laws.

You may not reproduce, distribute, modify, or create derivative works of any part of the Service without our express written permission.`,
  },
  {
    id: 'privacy',
    title: '9. Privacy',
    body: `Your use of the Service is also governed by our Privacy Policy, available at ${WEBSITE}/privacy. The Privacy Policy describes how we collect, use, and protect your personal data and forms part of these Terms.`,
  },
  {
    id: 'disclaimers',
    title: '10. Disclaimers',
    body: `The Service is provided "as is" and "as available" without warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.

We do not warrant that:
• The Service will be uninterrupted, error-free, or free of viruses
• The results obtained through the Service will be accurate or reliable
• Any defects in the Service will be corrected

Fitness and health data provided through the Service is for informational purposes only and does not constitute medical advice. Always consult a qualified health professional before beginning any exercise or nutrition programme.`,
  },
  {
    id: 'liability',
    title: '11. Limitation of Liability',
    body: `To the maximum extent permitted by applicable law, ${CONTROLLER} and its directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Service, even if advised of the possibility of such damages.

Our total aggregate liability to you for any claim arising out of or relating to these Terms or the Service shall not exceed the greater of (a) the total amount you paid to us in the 12 months preceding the claim, or (b) £100 (one hundred pounds sterling).

Nothing in these Terms limits liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded by law.

UK and EU Consumers: Nothing in these Terms affects your statutory rights as a consumer.`,
  },
  {
    id: 'termination',
    title: '12. Termination',
    body: `You may delete your account at any time by contacting us at ${CONTACT}. Upon deletion, your data is handled as described in our Privacy Policy.

We may suspend or terminate your access to the Service immediately and without notice if you breach these Terms, if required by law, or if we cease providing the Service.

Upon termination, all licences granted to you under these Terms will immediately cease. Sections that by their nature should survive termination (including Sections 7, 8, 10, 11, and 13) will survive.`,
  },
  {
    id: 'governing-law',
    title: '13. Governing Law & Disputes',
    body: `These Terms are governed by the laws of England and Wales. Any dispute arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.

If you are a consumer in the EU, you may also be entitled to bring a claim in the courts of your country of residence.

EU consumers may also use the European Commission's Online Dispute Resolution platform at https://ec.europa.eu/consumers/odr/.

Before raising a formal dispute, please contact us at ${CONTACT} — we will attempt to resolve the matter informally within 30 days.`,
  },
  {
    id: 'general',
    title: '14. General',
    body: `Entire Agreement: These Terms, together with the Privacy Policy, constitute the entire agreement between you and ${CONTROLLER} regarding the Service.

Severability: If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue in full force.

Waiver: Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.

Assignment: You may not assign or transfer your rights under these Terms without our written consent. We may assign our rights without restriction.

Contact: For questions about these Terms, email ${CONTACT}.`,
  },
];

export default function Terms() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(null);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <div className="page-header">
        <button className="icon-btn" style={{ marginRight: 4 }} onClick={() => navigate(-1)}>
          <span style={{ fontSize: 22, color: 'var(--color-text)' }}>‹</span>
        </button>
        <h1 style={{ flex: 1 }}>Terms of Service</h1>
      </div>

      <div className="page-content" style={{ maxWidth: 720, margin: '0 auto' }}>

        <div className="card" style={{ marginBottom: 20, borderLeft: '3px solid var(--color-primary)' }}>
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 6 }}>Last updated: {LAST_UPDATED}</div>
          <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--color-text)' }}>
            These Terms govern your use of <strong>{APP_NAME}</strong> operated by <strong>{CONTROLLER}</strong>.
            Please read them carefully before using the Service.
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['🇬🇧 English Law', '📋 Clear & Fair', '✉️ ' + CONTACT].map(badge => (
              <span key={badge} style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 12, background: 'rgba(215,38,56,0.1)', color: 'var(--color-primary)', border: '1px solid rgba(215,38,56,0.2)' }}>
                {badge}
              </span>
            ))}
          </div>
        </div>

        {sections.map(sec => (
          <div key={sec.id} className="card" style={{ marginBottom: 10 }}>
            <button
              onClick={() => setActiveSection(activeSection === sec.id ? null : sec.id)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}>
              <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--color-text)' }}>{sec.title}</span>
              <span style={{ fontSize: 18, color: 'var(--color-text-muted)', transition: 'transform 0.2s', transform: activeSection === sec.id ? 'rotate(180deg)' : 'none' }}>
                ›
              </span>
            </button>

            {activeSection === sec.id && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--color-border)', fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.75, whiteSpace: 'pre-line' }}>
                {sec.body}
              </div>
            )}
          </div>
        ))}

        <div style={{ textAlign: 'center', padding: '24px 0 40px', fontSize: 13, color: 'var(--color-text-muted)' }}>
          Questions? Email us at{' '}
          <a href={`mailto:${CONTACT}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{CONTACT}</a>
        </div>
      </div>
    </div>
  );
}
